import { useEffect, useRef, useState } from 'react';
import { api, type FashionProfile, type Outfit, type OutfitItem, type OutfitSuggestion } from '../api';
import { t, tn, locale, getLang } from '../i18n';

// ─────────────────────────────────────────────────────────────────
// Outfit-förslag från Q (api/src/outfits). OutfitCards visar ett förslag och
// kan återanvändas i Q-chatten (action 'outfits'); OutfitsView är fliken
// Outfits i garderoben med fritext och valfri bild.
// ─────────────────────────────────────────────────────────────────

const C = {
  text: '#111111',
  textMuted: '#8f8a8c',
  border: '#e8e6e7',
  surface: '#ffffff',
  soft: '#f6f2ec',
  rose: 'hsl(var(--primary))',
  danger: 'hsl(var(--destructive))',
};

const fmt = (n: number) => Math.round(n).toLocaleString(locale());

/** Totalpriset som text: köp och hyra var för sig, eller att allt redan är ens eget. */
export function totalsText(totals: Outfit['totals']): string {
  const parts: string[] = [];
  if (totals.buy > 0) parts.push(t('outfits.totalBuy', { amount: fmt(totals.buy) }));
  if (totals.rentPerMonth > 0) parts.push(t('outfits.totalRent', { amount: fmt(totals.rentPerMonth) }));
  return parts.length ? parts.join(' + ') : t('outfits.totalOwnOnly');
}

function priceText(item: OutfitItem): string {
  if (item.acquire === 'buy' && item.price != null) return t('outfits.priceBuy', { amount: fmt(item.price) });
  if (item.acquire === 'rent' && item.rentPerMonth != null) return t('outfits.priceRent', { amount: fmt(item.rentPerMonth) });
  return '';
}

// Samma event som openListing() i App.tsx — öppnar annonsen var man än är.
function openItem(id: string) {
  window.dispatchEvent(new CustomEvent<string>('qvestium:open-listing', { detail: id }));
}

const BADGE: Record<OutfitItem['acquire'], { bg: string; color: string }> = {
  own: { bg: '#eef3ee', color: '#2e7d32' },
  buy: { bg: 'hsl(var(--primary) / 0.1)', color: 'hsl(var(--primary))' },
  rent: { bg: '#eef1f8', color: '#33508a' },
};

function ItemRow({ item }: { item: OutfitItem }) {
  const badge = BADGE[item.acquire];
  const content = (
    <>
      <div style={{ width: 44, height: 44, flexShrink: 0, background: C.soft, borderRadius: 4, overflow: 'hidden' }}>
        {item.imageUrl && <img src={item.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
        <div style={{ fontSize: 12, color: C.textMuted }}>{priceText(item)}</div>
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: badge.bg, color: badge.color, flexShrink: 0 }}>
        {t(`outfits.${item.acquire}`)}
      </span>
    </>
  );
  const rowStyle = { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', textDecoration: 'none', color: 'inherit' } as const;
  return (
    <li style={{ listStyle: 'none' }}>
      <a href={item.link} data-acquire={item.acquire} onClick={e => { e.preventDefault(); openItem(item.id); }} style={rowStyle}>{content}</a>
    </li>
  );
}

function OutfitCard({ outfit, index, suggestionId, hasBodyPhoto, onGoToProfile }: {
  outfit: Outfit; index: number; suggestionId: string; hasBodyPhoto: boolean; onGoToProfile?: () => void;
}) {
  const [renderUrl, setRenderUrl] = useState<string | null>(null);
  const [showRender, setShowRender] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => () => { if (renderUrl) URL.revokeObjectURL(renderUrl); }, [renderUrl]);

  async function tryOn() {
    if (renderUrl) { setShowRender(true); return; }
    setBusy(true);
    setError(null);
    try {
      setRenderUrl(await api.tryOnOutfit(suggestionId, index));
      setShowRender(true);
    } catch (err: any) {
      setError(err?.message ?? t('outfits.tryOnFailed'));
    } finally {
      setBusy(false);
    }
  }

  const image = showRender && renderUrl ? renderUrl : outfit.collageUrl;
  return (
    <article style={{ border: `1px solid ${C.border}`, borderRadius: 8, background: C.surface, overflow: 'hidden' }}>
      {image && (
        <div style={{ background: C.soft, aspectRatio: showRender && renderUrl ? '2 / 3' : '1 / 1' }}>
          <img src={image} alt={outfit.title} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
        </div>
      )}
      <div style={{ padding: 14 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: C.text }}>{outfit.title}</h3>
        {outfit.why && <p style={{ margin: '4px 0 8px', fontSize: 13, color: C.textMuted }}>{outfit.why}</p>}
        <ul style={{ margin: 0, padding: 0 }}>
          {outfit.items.map(item => <ItemRow key={item.id} item={item} />)}
        </ul>
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}`, fontSize: 14, fontWeight: 600, color: C.text }} data-testid="outfit-total">
          {totalsText(outfit.totals)}
        </div>
        <div style={{ marginTop: 10 }}>
          {hasBodyPhoto ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {showRender && renderUrl ? (
                <button type="button" onClick={() => setShowRender(false)} style={buttonStyle(false)}>{t('outfits.showCollage')}</button>
              ) : (
                <button type="button" onClick={tryOn} disabled={busy} style={buttonStyle(true, busy)}>
                  {busy ? t('outfits.tryingOn') : renderUrl ? t('outfits.showOnMe') : t('outfits.tryOn')}
                </button>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: C.textMuted }}>
              {tn('outfits.noBodyPhoto', {
                link: onGoToProfile
                  ? <button type="button" onClick={onGoToProfile} style={{ background: 'none', border: 'none', padding: 0, color: C.rose, textDecoration: 'underline', cursor: 'pointer', fontSize: 12 }}>{t('outfits.yourProfile')}</button>
                  : t('outfits.yourProfile'),
              })}
            </div>
          )}
          {showRender && renderUrl && <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6 }}>{t('outfits.aiDisclaimer')}</div>}
          {error && <div style={{ fontSize: 12, color: C.danger, marginTop: 6 }}>{error}</div>}
        </div>
      </div>
    </article>
  );
}

function buttonStyle(primary: boolean, busy = false) {
  return {
    padding: '8px 14px', fontSize: 13, fontWeight: 600, borderRadius: 4, cursor: busy ? 'wait' : 'pointer',
    border: primary ? 'none' : `1px solid ${C.border}`, background: primary ? C.text : C.surface, color: primary ? '#fff' : C.text,
    opacity: busy ? 0.7 : 1, maxWidth: '100%',
  } as const;
}

/** Ett förslag: tolkningen och 2–3 outfitkort. */
export function OutfitCards({ suggestion, onGoToProfile }: { suggestion: OutfitSuggestion; onGoToProfile?: () => void }) {
  return (
    <div>
      {suggestion.brief.summary && (
        <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 12 }}>
          <strong style={{ color: C.text }}>{t('outfits.interpreted')}:</strong> {suggestion.brief.summary}
        </div>
      )}
      {suggestion.outfits.length === 0 ? (
        <div style={{ fontSize: 14, color: C.textMuted, padding: '24px 0' }}>{t('outfits.none')}</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))', gap: 16 }}>
          {suggestion.outfits.map((o, i) => (
            <OutfitCard key={`${suggestion.id}-${i}`} outfit={o} index={i} suggestionId={suggestion.id} hasBodyPhoto={suggestion.hasBodyPhoto} onGoToProfile={onGoToProfile} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Fliken Outfits i garderoben. */
export function OutfitsView({ profile, onGoToProfile }: { profile: FashionProfile | null; onGoToProfile?: () => void }) {
  const [prompt, setPrompt] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<OutfitSuggestion | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function pickImage(file: File | undefined) {
    if (!file) return;
    setError(null);
    try {
      setImageUrl((await api.uploadFile(file)).url);
    } catch {
      setError(t('outfits.imageUploadFailed'));
    }
  }

  async function submit() {
    if (busy || (!prompt.trim() && !imageUrl)) return;
    setBusy(true);
    setError(null);
    try {
      setSuggestion(await api.suggestOutfits({ prompt: prompt.trim(), imageUrl: imageUrl ?? undefined, lang: getLang() }));
    } catch (err: any) {
      setError(err?.message ?? t('outfits.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ maxWidth: 960 }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 4px', color: C.text }}>{t('outfits.title')}</h2>
      <p style={{ fontSize: 13, color: C.textMuted, margin: '0 0 12px' }}>{t('outfits.intro')}</p>
      <textarea
        value={prompt}
        onChange={e => setPrompt(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(); }}
        placeholder={t('outfits.placeholder')}
        rows={3}
        maxLength={1000}
        style={{ width: '100%', boxSizing: 'border-box', padding: 10, fontSize: 14, border: `1px solid ${C.border}`, borderRadius: 4, resize: 'vertical', fontFamily: 'inherit' }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={e => { pickImage(e.target.files?.[0]); e.target.value = ''; }} />
        {imageUrl ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <img src={imageUrl} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4 }} />
            <button type="button" onClick={() => setImageUrl(null)} style={buttonStyle(false)}>{t('outfits.removeImage')}</button>
          </span>
        ) : (
          <button type="button" onClick={() => fileRef.current?.click()} style={buttonStyle(false)}>{t('outfits.addImage')}</button>
        )}
        <button type="button" onClick={submit} disabled={busy || (!prompt.trim() && !imageUrl)} style={buttonStyle(true, busy)}>
          {busy ? t('outfits.working') : t('outfits.submit')}
        </button>
      </div>
      {error && <div style={{ fontSize: 13, color: C.danger, marginTop: 8 }}>{error}</div>}
      {suggestion && (
        <div style={{ marginTop: 20 }}>
          <OutfitCards suggestion={{ ...suggestion, hasBodyPhoto: suggestion.hasBodyPhoto || (profile?.bodyPhotos?.length ?? 0) > 0 }} onGoToProfile={onGoToProfile} />
        </div>
      )}
    </div>
  );
}
